import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';

/**
 * Holds rendering of `children` until {@link AuthProvider} finishes its
 * async init. Without this, pages that fetch on mount race the auth
 * handshake — `getIdToken()` returns null before Firebase resolves, the
 * request goes out unauthenticated, the server 401s, and offline-mode
 * fallbacks fire on a perfectly healthy server (e.g. landing directly
 * on `/pulse/play/on-and-on` from a cold tab).
 *
 * Centralising the gate here removes the same loading/error block that
 * used to be duplicated across the home / mode-picker pages and
 * protects every current and future authed route in one shot.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { status, error } = useAuth();
  if (status === 'initialising') {
    return (
      <div className="loading-screen">
        <h2>Booting up the grid...</h2>
        <p className="muted">Signing you in anonymously.</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="error-box">
        <strong>Failed to initialise.</strong>
        <p>{error}</p>
        {/*
          Dev hint only — in production this dotenv pointer would just
          confuse end users (and is wrong for the test-mode/?testUid
          failure path), so keep it scoped to local development where
          it's the most likely cause.
        */}
        {import.meta.env.DEV && (
          <p>Check that VITE_FIREBASE_* are set in your .env.</p>
        )}
      </div>
    );
  }
  return <>{children}</>;
}
